import { Box, Button, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { useLocale } from '@repo/i18n';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CartItem } from '@repo/types';

interface WaitingListSuccessViewProps {
    cartItems: CartItem[];
    onClose?: () => void;
}

export function WaitingListSuccessView({ cartItems, onClose }: WaitingListSuccessViewProps) {
    const theme = useTheme();
    const { t } = useLocale();

    return (
        <Card>
            <CardContent>
                <Stack spacing={4} alignItems="center">
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <CheckCircleIcon
                            sx={{
                                color: theme.palette.success.main,
                                fontSize: 64,
                                mb: 2,
                            }}
                        />
                        <Typography variant="h4" component="h1" gutterBottom>
                            {t.successful}!
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {t.waiting_list_notification_message}
                        </Typography>
                    </Box>

                    {/* Waiting List Items Summary */}
                    <Box sx={{ width: '100%', my: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            {t.waiting_list} {t.summary}
                        </Typography>
                        {cartItems.map((item, index) => (
                            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                                <CardContent>
                                    {item.products?.map((product, pIndex) => (
                                        <Box key={pIndex} sx={{ mb: 2 }}>
                                            <Typography variant="subtitle1" component="div">
                                                {product.product?.title ||
                                                    product.productCollectionTitle ||
                                                    product.product?.shortDescription ||
                                                    'Tour ID: ' + product.id}
                                            </Typography>
                                            {item.ticketOptions?.map((option, oIndex) => (
                                                <Typography
                                                    key={oIndex}
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {option.name}: {option.quantity}x
                                                </Typography>
                                            ))}
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        ))}
                    </Box>

                    {/* Next Steps */}
                    <Box sx={{ width: '100%', textAlign: 'center' }}>
                        <Typography variant="body1" paragraph>
                            {t.waiting_list_what_happens_next}
                        </Typography>
                        {t.waiting_list_next_steps.split('|').map((step, index) => (
                            <Typography
                                key={index}
                                variant="body2"
                                color="text.secondary"
                                paragraph
                            >
                                {index + 1}. {step}
                            </Typography>
                        ))}
                    </Box>

                    {/* Close Button */}
                    {onClose && (
                        <Button variant="contained" onClick={onClose} sx={{ minWidth: 200 }}>
                            {t.close}
                        </Button>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
